import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Plus, Minus, Equal } from "lucide-react";
import { HoldingComparison } from "../hooks/useHoldingsComparisonController";

interface ComparisonTableProps {
  comparisonData: HoldingComparison[];
  isLoading: boolean;
}

export const ComparisonTable: React.FC<ComparisonTableProps> = ({
  comparisonData,
  isLoading,
}) => {
  // Helper function to format currency amounts
  const formatCurrency = (amount: number, currency: string = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  // Helper function to format percentage
  const formatPercentage = (percentage: number | undefined) => {
    if (percentage === undefined || !isFinite(percentage)) return "-";
    return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(2)}%`;
  };

  // Helper function to get status badge
  const getStatusBadge = (status: HoldingComparison['status']) => {
    switch (status) {
      case 'new':
        return (
          <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
            <Plus className="h-3 w-3 mr-1" />
            New
          </Badge>
        );
      case 'removed':
        return (
          <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-200">
            <Minus className="h-3 w-3 mr-1" />
            Removed
          </Badge>
        );
      case 'changed':
        return (
          <Badge variant="default" className="bg-blue-100 text-blue-800 border-blue-200">
            <TrendingUp className="h-3 w-3 mr-1" />
            Changed
          </Badge>
        );
      case 'unchanged':
        return (
          <Badge variant="outline" className="bg-gray-50 text-gray-600">
            <Equal className="h-3 w-3 mr-1" />
            Unchanged
          </Badge>
        );
      default:
        return null;
    }
  };

  // Helper function to get change indicator
  const getChangeIndicator = (change: number | undefined) => {
    if (change === undefined || change === 0) return null;
    
    return change > 0 ? (
      <TrendingUp className="h-4 w-4 text-green-600" />
    ) : (
      <TrendingDown className="h-4 w-4 text-red-600" />
    );
  };

  // Helper function to format change with color
  const formatChangeWithColor = (change: number | undefined, isCurrency: boolean = false, currency: string = "USD") => {
    if (change === undefined || change === 0) return <span className="text-muted-foreground">-</span>;
    
    const formatted = isCurrency ? formatCurrency(Math.abs(change), currency) : Math.abs(change).toLocaleString();
    const colorClass = change > 0 ? 'text-green-600' : 'text-red-600';
    const prefix = change > 0 ? '+' : '-';
    
    return <span className={colorClass}>{prefix}{formatted}</span>;
  };

  // Helper function to get currency for amount changes
  const getChangeCurrency = (comparison: HoldingComparison) => {
    // Priority: primary holding currency, then secondary holding currency, then USD
    return comparison.primaryHolding?.currency || 
           comparison.secondaryHolding?.currency || 
           "USD";
  };

  return (
    <div className="max-h-[80vh] overflow-auto">
      <Table>
        <TableHeader className="sticky top-0 bg-background z-10">
          <TableRow>
            <TableHead>Instrument</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Primary Qty</TableHead>
            <TableHead className="text-right">Secondary Qty</TableHead>
            <TableHead className="text-right">Qty Change</TableHead>
            <TableHead className="text-right">Primary Amount</TableHead>
            <TableHead className="text-right">Secondary Amount</TableHead>
            <TableHead className="text-right">Amount Change</TableHead>
            <TableHead className="text-right">% Change</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={9} className="text-center py-4">
                Loading comparison data...
              </TableCell>
            </TableRow>
          ) : comparisonData.length > 0 ? (
            comparisonData.map((comparison) => (
              <TableRow key={comparison.instrumentKey}>
                <TableCell>
                  <div>
                    <div className="font-medium">{comparison.instrumentName}</div>
                    <div className="text-sm text-muted-foreground">{comparison.instrumentKey}</div>
                  </div>
                </TableCell>
                <TableCell>
                  {getStatusBadge(comparison.status)}
                </TableCell>
                <TableCell className="text-right">
                  {comparison.primaryHolding ? (
                    comparison.primaryHolding.quantity.toLocaleString()
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {comparison.secondaryHolding ? (
                    comparison.secondaryHolding.quantity.toLocaleString()
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {getChangeIndicator(comparison.quantityChange)}
                    {formatChangeWithColor(comparison.quantityChange)}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {comparison.primaryHolding ? (
                    formatCurrency(comparison.primaryHolding.baseAmount, comparison.primaryHolding.currency)
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {comparison.secondaryHolding ? (
                    formatCurrency(comparison.secondaryHolding.baseAmount, comparison.secondaryHolding.currency)
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {getChangeIndicator(comparison.amountChange)}
                    {formatChangeWithColor(comparison.amountChange, true, getChangeCurrency(comparison))}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <span className={
                    comparison.percentageChange === undefined ? 'text-muted-foreground' :
                    comparison.percentageChange > 0 ? 'text-green-600' :
                    comparison.percentageChange < 0 ? 'text-red-600' : 'text-muted-foreground'
                  }>
                    {formatPercentage(comparison.percentageChange)}
                  </span>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={9} className="text-center py-4">
                No holdings comparison data available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}; 